import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * DiscoveryPanel — Yeni Enstrümanlar sekmesi (EK İŞ A)
 * ÜST: PENDING adaylar — Onayla ve Ekle / Yoksay
 * ALT: Son 24h eklenenler (price_gate_audit INITIAL_WRITE)
 */
export default function DiscoveryPanel() {
  const [candidates, setCandidates] = useState([]);
  const [ignored, setIgnored] = useState([]);
  const [showIgnored, setShowIgnored] = useState(false);
  const [recentAdditions, setRecentAdditions] = useState([]);
  const [approving, setApproving] = useState(null);
  const [approveForm, setApproveForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const pending = await axios.get(`${API}/admin/instruments/discovery?status=PENDING`, { headers });
      setCandidates(pending.data.candidates || []);
      const ign = await axios.get(`${API}/admin/instruments/discovery?status=IGNORED`, { headers });
      setIgnored(ign.data.candidates || []);
      // price_gate_audit direkt endpoint yok; simple query — use admin/all-positions type
      // Fallback: skip if endpoint missing
      try {
        const recent = await axios.get(`${API}/admin/price-gate-audit/recent?action=INITIAL_WRITE&hours=24`, { headers });
        setRecentAdditions(recent.data.audits || []);
      } catch {
        setRecentAdditions([]);
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Yükleme hatası: ' + (e.response?.data?.detail || e.message) });
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openApprove = (c) => {
    setApproving(c);
    setApproveForm({
      symbol: c.symbol, name: c.name, category: c.category || 'BIST_100',
      currency: 'TRY', listing_date: '', ipo_price: '',
    });
  };

  const doApprove = async () => {
    const body = { ...approveForm };
    if (!body.listing_date) delete body.listing_date;
    if (!body.ipo_price) delete body.ipo_price;
    else body.ipo_price = parseFloat(body.ipo_price);

    try {
      const r = await axios.post(`${API}/admin/instruments`, body, { headers });
      // Adaydan sil (backend'de onboard sonrası symbol assets'ta var → discovery filter'ı dışında)
      // Manuel silmek yerine PENDING'de tutmayalım — refresh
      await axios.post(`${API}/admin/instruments/discovery/${body.symbol}/ignore`, {}, { headers });
      setMessage({ type: 'success', text: `${body.symbol} eklendi (id=${r.data.asset?.id?.slice(0, 8)})` });
      setApproving(null);
      fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errCode = typeof detail === 'string' ? detail : (detail?.error || 'HATA');
      setMessage({ type: 'error', text: `${body.symbol}: ${errCode}` });
    }
  };

  const doIgnore = async (symbol) => {
    try {
      await axios.post(`${API}/admin/instruments/discovery/${symbol}/ignore`, {}, { headers });
      setMessage({ type: 'success', text: `${symbol} yoksayıldı` });
      fetchAll();
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  const runDiscovery = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/instruments/discovery/run`, {}, { headers });
      setMessage({ type: 'success', text: `Tarama: ${r.data.new_candidates} yeni aday, ${r.data.updated_last_seen} güncel` });
      fetchAll();
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  const list = showIgnored ? ignored : candidates;

  return (
    <div className="p-6 space-y-6" data-testid="discovery-panel">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Yeni Enstrümanlar</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowIgnored(!showIgnored)}
            className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
            data-testid="toggle-ignored-btn"
          >
            {showIgnored ? 'Bekleyenler' : `Yoksayılanlar (${ignored.length})`}
          </button>
          <button
            onClick={runDiscovery}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 disabled:opacity-50"
            data-testid="run-discovery-btn"
          >
            Tarama Yap
          </button>
        </div>
      </div>

      {message && (
        <div className={`p-3 rounded ${message.type === 'success' ? 'bg-green-900 text-green-200' : 'bg-red-900 text-red-200'}`} data-testid="discovery-message">
          {message.text}
        </div>
      )}

      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-900">
            <tr className="text-gray-400 text-left text-sm">
              <th className="p-3">Sembol</th>
              <th className="p-3">İsim</th>
              <th className="p-3">İlk Görülme</th>
              <th className="p-3">Son Görülme</th>
              <th className="p-3">Aksiyon</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-gray-500">
                {showIgnored ? 'Yoksayılan aday yok' : 'Bekleyen aday yok'}
              </td></tr>
            )}
            {list.map(c => (
              <tr key={c.symbol} className="border-t border-gray-700 text-white" data-testid={`candidate-row-${c.symbol}`}>
                <td className="p-3 font-mono font-bold">{c.symbol}</td>
                <td className="p-3">{c.name || '-'}</td>
                <td className="p-3 text-xs text-gray-400">{new Date(c.first_seen).toLocaleString('tr-TR')}</td>
                <td className="p-3 text-xs text-gray-400">{new Date(c.last_seen).toLocaleString('tr-TR')}</td>
                <td className="p-3">
                  {!showIgnored ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => openApprove(c)}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-500"
                        data-testid={`approve-btn-${c.symbol}`}
                      >
                        Onayla ve Ekle
                      </button>
                      <button
                        onClick={() => doIgnore(c.symbol)}
                        className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-500"
                        data-testid={`ignore-btn-${c.symbol}`}
                      >
                        Yoksay
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={async () => {
                        // reactivate ignored: sadece silme yeterli — status PENDING'e döndür
                        try {
                          await axios.post(`${API}/admin/instruments/discovery/run`, {}, { headers });
                          fetchAll();
                        } catch { /* noop */ }
                      }}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded"
                      data-testid={`restore-btn-${c.symbol}`}
                    >
                      Geri Al
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Approve Modal */}
      {approving && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" data-testid="approve-modal">
          <div className="bg-gray-800 p-6 rounded-lg w-96 space-y-3">
            <h3 className="text-xl font-bold text-white">{approving.symbol} Ekle</h3>
            <div>
              <label className="text-gray-400 text-sm">Kategori</label>
              <input value={approveForm.category} onChange={e => setApproveForm({...approveForm, category: e.target.value})}
                className="w-full bg-gray-700 text-white p-2 rounded" data-testid="approve-category" />
            </div>
            <div>
              <label className="text-gray-400 text-sm">Currency</label>
              <select value={approveForm.currency} onChange={e => setApproveForm({...approveForm, currency: e.target.value})}
                className="w-full bg-gray-700 text-white p-2 rounded" data-testid="approve-currency">
                <option>TRY</option>
                <option>USD</option>
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-sm">Halka Arz Fiyatı (opsiyonel)</label>
              <input type="number" step="0.01" value={approveForm.ipo_price} onChange={e => setApproveForm({...approveForm, ipo_price: e.target.value})}
                className="w-full bg-gray-700 text-white p-2 rounded" data-testid="approve-ipo-price" />
            </div>
            <div>
              <label className="text-gray-400 text-sm">Halka Arz Tarihi (opsiyonel)</label>
              <input type="date" value={approveForm.listing_date} onChange={e => setApproveForm({...approveForm, listing_date: e.target.value})}
                className="w-full bg-gray-700 text-white p-2 rounded" data-testid="approve-listing-date" />
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={doApprove} className="flex-1 bg-green-600 text-white py-2 rounded font-bold" data-testid="approve-confirm">Onayla</button>
              <button onClick={() => setApproving(null)} className="flex-1 bg-gray-600 text-white py-2 rounded" data-testid="approve-cancel">İptal</button>
            </div>
          </div>
        </div>
      )}

      {/* Son 24 saat eklenenler */}
      <div className="mt-8">
        <h3 className="text-lg font-bold text-white mb-3">Son 24 Saatte Eklenenler</h3>
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-900">
              <tr className="text-gray-400 text-left text-sm">
                <th className="p-3">Sembol</th>
                <th className="p-3">Eklenme</th>
                <th className="p-3">İlk Fiyat</th>
                <th className="p-3">Güncel</th>
                <th className="p-3">Kalite</th>
              </tr>
            </thead>
            <tbody>
              {recentAdditions.length === 0 ? (
                <tr><td colSpan={5} className="p-4 text-center text-gray-500">
                  Endpoint henüz yok veya son 24 saatte eklenmiş yok
                </td></tr>
              ) : recentAdditions.map((a, i) => (
                <tr key={i} className="border-t border-gray-700 text-white text-sm">
                  <td className="p-3 font-mono">{a.symbol}</td>
                  <td className="p-3 text-xs">{new Date(a.ts).toLocaleString('tr-TR')}</td>
                  <td className="p-3">{a.new_price}</td>
                  <td className="p-3">{a.current_price}</td>
                  <td className="p-3">
                    <span className={a.price_quality === 'OK' ? 'text-green-400' : 'text-yellow-400'}>
                      {a.price_quality}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
